import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

export interface BulkAction {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: (selectedIds: string[]) => void | Promise<void>;
  /** Primary action ends up rendered with the gold variant. */
  primary?: boolean;
  destructive?: boolean;
  disabled?: boolean;
}

export interface BulkActionBarProps {
  selectedIds: string[];
  totalRows?: number;
  onClear: () => void;
  actions: BulkAction[];
  label?: string;
  className?: string;
}

/**
 * UX Enhancements §12. Selection toolbar that surfaces when one or more
 * rows are checked. Designed to float inside a DataTable's wrapper or at
 * the top of a list page.
 */
export function BulkActionBar({
  selectedIds,
  totalRows,
  onClear,
  actions,
  label,
  className,
}: BulkActionBarProps) {
  if (selectedIds.length === 0) return null;

  return (
    <div
      role="region"
      aria-label={label ?? "Bulk actions"}
      className={cn(
        "flex items-center justify-between gap-space-3 flex-wrap",
        "bg-onyx text-white-pure rounded-md px-space-3 py-space-2",
        "border border-gold/40 shadow-gold",
        className
      )}
    >
      <div className="flex items-center gap-space-2">
        <span className="inline-flex items-center gap-space-1 font-display text-label font-semibold uppercase tracking-label text-gold">
          {selectedIds.length}
          <span className="text-white-pure/70">
            {selectedIds.length === 1 ? "item selected" : "items selected"}
            {typeof totalRows === "number" ? ` of ${totalRows}` : ""}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-space-2 flex-wrap">
        {actions.map((action) => (
          <Button
            key={action.key}
            variant={action.destructive ? "destructive" : action.primary ? "primary" : "secondary"}
            size="sm"
            disabled={action.disabled}
            onClick={() => action.onClick(selectedIds)}
          >
            {action.icon}
            {action.label}
          </Button>
        ))}
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white-pure/70 hover:text-white-pure hover:bg-white-pure/5"
        >
          <X size={16} strokeWidth={1.5} aria-hidden />
        </button>
      </div>
    </div>
  );
}
